import { createContext, useContext, useState, useCallback, type ReactNode } from "react"

export type ChatStatus = "submitted" | "streaming" | "ready" | "error"

export interface ChatLoadingContextType {
  isLoading: boolean
  isStreaming: boolean
  isManuallyStopped: boolean
  status: ChatStatus
  error: Error | undefined
  setLoading: (loading: boolean) => void
  setStreaming: (streaming: boolean) => void
  setManuallyStopped: (stopped: boolean) => void
  setStatus: (status: ChatStatus) => void
  setError: (error: Error | undefined) => void
  stopManually: () => void
  reset: () => void
}

const ChatLoadingContext = createContext<ChatLoadingContextType | undefined>(undefined)

interface ChatLoadingProviderProps {
  children: ReactNode
}

export function ChatLoadingProvider({ children }: ChatLoadingProviderProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [isStreaming, setIsStreaming] = useState(false)
  const [isManuallyStopped, setIsManuallyStopped] = useState(false)
  const [status, setStatus] = useState<ChatStatus>("ready")
  const [error, setError] = useState<Error | undefined>(undefined)

  const setLoading = useCallback((loading: boolean) => {
    setIsLoading(loading)
  }, [])

  const setStreaming = useCallback((streaming: boolean) => {
    setIsStreaming(streaming)
  }, [])

  const setManuallyStopped = useCallback((stopped: boolean) => {
    setIsManuallyStopped(stopped)
  }, [])

  const stopManually = useCallback(() => {
    setIsManuallyStopped(true)
    setIsLoading(false)
    setIsStreaming(false)
  }, [])

  const reset = useCallback(() => {
    setIsLoading(false)
    setIsStreaming(false)
    setIsManuallyStopped(false)
    setStatus("ready")
    setError(undefined)
  }, [])

  const value: ChatLoadingContextType = {
    isLoading,
    isStreaming,
    isManuallyStopped,
    status,
    error,
    setLoading,
    setStreaming,
    setManuallyStopped,
    setStatus,
    setError,
    stopManually,
    reset
  }

  return (
    <ChatLoadingContext.Provider value={value}>
      {children}
    </ChatLoadingContext.Provider>
  )
}

export function useChatLoading() {
  const context = useContext(ChatLoadingContext)
  if (context === undefined) {
    throw new Error("useChatLoading must be used within a ChatLoadingProvider")
  }
  return context
} 